import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";

import {
  initialState,
  createInitialState,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper
} from "helpers/reducerHelpers";

// The report endpoints return plain row hashes (not JSONAPI envelopes), so we
// store them directly rather than running them through objectSerializer.
const applyList = (state, action, dataKey) => {
  state.loading = false;
  const data = state[dataKey];
  if (!data) return;

  const payload = action.payload || {};
  data.data = Array.isArray(payload.rows) ? payload.rows : [];
  data.totalRows = Number.isFinite(payload.total_rows) ? payload.total_rows : 0;
  data.totals = payload.totals || {};
};

const contractUrl = `/crm/reports/contract`;
const addendumUrl = `/crm/reports/addendum`;
const combinedUrl = `/crm/reports/combined`;

// ── List fetches ──────────────────────────────────────────────────────────────
// Both endpoints return { rows, total_rows, totals } and are stored via the
// shared handleFulfilled helper (reads rows / total_rows).

export const getContracts = createAsyncThunk(
  "ReportContract/getContracts",
  async (params) => {
    try {
      const response = await apiAxios.get(contractUrl, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const getAddendums = createAsyncThunk(
  "ReportContract/getAddendums",
  async (params) => {
    try {
      const response = await apiAxios.get(addendumUrl, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const getCombined = createAsyncThunk(
  "ReportContract/getCombined",
  async (params) => {
    try {
      const response = await apiAxios.get(combinedUrl, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const getContractSummary = createAsyncThunk(
  "ReportContract/getContractSummary",
  async (params) => {
    try {
      const response = await apiAxios.get(`${contractUrl}/summary`, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// ── Exports (blob download) ───────────────────────────────────────────────────

const downloadBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const exportContracts = createAsyncThunk(
  "ReportContract/exportContracts",
  async (params) => {
    const response = await apiAxios.get(`${contractUrl}/export`, { params, responseType: "blob" });
    downloadBlob(response.data, "contracts.xlsx");
  }
);

export const exportAddendums = createAsyncThunk(
  "ReportContract/exportAddendums",
  async (params) => {
    const response = await apiAxios.get(`${addendumUrl}/export`, { params, responseType: "blob" });
    downloadBlob(response.data, "addendums.xlsx");
  }
);

export const exportCombined = createAsyncThunk(
  "ReportContract/exportCombined",
  async (params) => {
    const response = await apiAxios.get(`${combinedUrl}/export`, { params, responseType: "blob" });
    downloadBlob(response.data, "contracts.xlsx");
  }
);

const isRejectedAction = (action) => (action.type.startsWith("ReportContract/")) && action.type.endsWith("/rejected");

const contractSlice = createSlice({
  name: "report_contract",
  initialState: {
    ...initialState,
    addendumData: createInitialState(),
    combinedData: createInitialState(),
    summaryData: {}
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getContracts.pending, (state) => { state.loading = true; })
      .addCase(getContracts.fulfilled, (state, action) => applyList(state, action, "allData"))
      .addCase(getAddendums.pending, (state) => { state.loading = true; })
      .addCase(getAddendums.fulfilled, (state, action) => applyList(state, action, "addendumData"))
      .addCase(getCombined.pending, (state) => { state.loading = true; })
      .addCase(getCombined.fulfilled, (state, action) => applyList(state, action, "combinedData"))
      .addCase(getContractSummary.fulfilled, (state, action) => {
        state.summaryData = action.payload;
      })
      .addCase(exportContracts.pending, (state) => { state.exporting = true; })
      .addCase(exportContracts.fulfilled, (state) => {
        state.exporting = false;
        toast.success("Contracts have been exported successfully.");
      })
      .addCase(exportAddendums.pending, (state) => { state.exporting = true; })
      .addCase(exportAddendums.fulfilled, (state) => {
        state.exporting = false;
        toast.success("Addendums have been exported successfully.");
      })
      .addCase(exportCombined.pending, (state) => { state.exporting = true; })
      .addCase(exportCombined.fulfilled, (state) => {
        state.exporting = false;
        toast.success("Combined report has been exported successfully.");
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        state.exporting = false;
        toast.error(action.payload?.message || action.payload?.error || action?.error?.message);
      });
  }
});

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = contractSlice.actions;

export default contractSlice.reducer;
